import produce from 'immer';

import { ActionType } from '../action-types';
import { Action } from '../actions';
import { Cell } from '../cell';

interface CellsState {
  loading: boolean;
  error: string | null;
  order: string[];
  data: {
    [key: string]: Cell;
  };
}

const test1: Cell = {
  id: 'test1',
  type: 'text',
  content: `
  # Markdown & JS(X) Editor
  This React app allows you to notate markdown, display resulting 
  notes, write javascript/jsx, and execute it live. Simply click
  any element to begin. 
  
  In the code editor, you may import 
  anything within the npm library, including CSS, and, using ESBuild, 
  it will be (almost) instantly bundled for auto-completion and execution. 
  \`console.log()\` goes to the browser console. Subsequent code 
  windows contain previous imports and declarations. 
  
  While the display window next to the code editor can be 
  selected with \`document.querySelector('#root')\` as normal, 
  a function \`show()\` has been added for your convenience.
  To the right of each cell, you can move or delete. 
  
  Before, after, and between item, you can hover to add a new cell.

  Enjoy!
  `,
};

const test2: Cell = {
  id: 'test2',
  type: 'code',
  content: `
  import React from 'react';
  import ReactDOM from 'react-dom';

  import 'bulma/css/bulma.css';

  const App = () => {
    return <div>Hello World!</div>;
  };

  show(<App />)
  `,
};

const initialState: CellsState = {
  loading: false,
  error: null,
  order: ['test1', 'test2'],
  data: { test1, test2 },
};

const reducer = produce((state: CellsState = initialState, action: Action) => {
  switch (action.type) {
    case ActionType.MOVE_CELL:
      const { direction } = action.payload;
      const index = state.order.findIndex((id) => id === action.payload.id);
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      // Handling out of bounds moves
      if (targetIndex < 0 || targetIndex > state.order.length - 1) {
        return state;
      }
      state.order[index] = state.order[targetIndex];
      state.order[targetIndex] = action.payload.id;
      return state;

    case ActionType.DELETE_CELL:
      delete state.data[action.payload];
      state.order = state.order.filter((id) => id !== action.payload);
      return state;

    case ActionType.INSERT_CELL_AFTER:
      const cell: Cell = {
        content: '',
        type: action.payload.type,
        id: randomId(),
      };
      state.data[cell.id] = cell;

      const foundIndex = state.order.findIndex(
        (id) => id === action.payload.id
      );

      if (foundIndex < 0) {
        state.order.unshift(cell.id);
      } else {
        state.order.splice(foundIndex + 1, 0, cell.id);
      }

      return state;

    case ActionType.UPDATE_CELL:
      const { id, content } = action.payload;
      state.data[id].content = content;
      return state;
    default:
      return state;
  }
});

const randomId = () => {
  return Math.random().toString(36).substr(2, 5);
};

export default reducer;
